import { useState } from 'react';
import type { EmploymentStatus, HouseholdType, HousingStatus, BenefitsInput } from '@/lib/benefits-calculator';
import {
  AppCheckbox as Checkbox,
  AppLabel as Label,
  AppSelect as Select,
  AppSelectContent as SelectContent,
  AppSelectItem as SelectItem,
  AppSelectTrigger as SelectTrigger,
  AppSelectValue as SelectValue,
  AppWebButton as Button,
  AppWebInput as Input,
} from '../../../ui-foundation/adapters/web';

interface BenefitsFormProps {
  defaultPostcode: string;
  defaultIncome: number;
  defaultChildAges: number[];
  defaultEmploymentStatus: EmploymentStatus;
  onSubmit: (input: BenefitsInput) => void;
}

export default function BenefitsForm({
  defaultPostcode,
  defaultIncome,
  defaultChildAges,
  defaultEmploymentStatus,
  onSubmit,
}: BenefitsFormProps) {
  const [postcode, setPostcode] = useState(defaultPostcode);
  const [income, setIncome] = useState(defaultIncome ? String(defaultIncome) : '');
  const [householdType, setHouseholdType] = useState<HouseholdType>(
    defaultChildAges.length > 0 ? 'single-with-children' : 'single'
  );
  const [childAgesStr, setChildAgesStr] = useState(defaultChildAges.join(', '));
  const [housingStatus, setHousingStatus] = useState<HousingStatus>('renting');
  const [employmentStatus, setEmploymentStatus] = useState<EmploymentStatus>(defaultEmploymentStatus);
  const [hasHealthCondition, setHasHealthCondition] = useState(false);
  const [providesCare, setProvidesCare] = useState(false);

  const hasChildren = householdType.includes('children');

  const handleSubmit = () => {
    const ages = hasChildren
      ? childAgesStr.split(',').map(a => parseInt(a.trim())).filter(a => !isNaN(a))
      : [];
    onSubmit({
      postcode,
      income: Number(income) || 0,
      householdType,
      childAges: ages,
      housingStatus,
      employmentStatus,
      hasHealthCondition,
      providesCare,
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border p-5 space-y-4">
      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">UK postcode</Label>
        <Input
          placeholder="e.g. SW1A 1AA"
          value={postcode}
          onChange={e => setPostcode(e.target.value)}
          autoComplete="postal-code"
          autoCapitalize="characters"
          spellCheck={false}
        />
      </div>

      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">Approximate annual household income</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
          <Input className="pl-7" placeholder="25,000" type="number" value={income} onChange={e => setIncome(e.target.value)} />
        </div>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">Household type</Label>
        <Select value={householdType} onValueChange={(v) => setHouseholdType(v as HouseholdType)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single">Single, no children</SelectItem>
            <SelectItem value="couple">Couple, no children</SelectItem>
            <SelectItem value="single-with-children">Single parent</SelectItem>
            <SelectItem value="couple-with-children">Couple with children</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {hasChildren && (
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Children's ages (comma-separated)</Label>
          <Input
            placeholder="e.g. 2, 5, 11"
            value={childAgesStr}
            onChange={e => setChildAgesStr(e.target.value)}
            spellCheck={false}
          />
        </div>
      )}

      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">Housing status</Label>
        <Select value={housingStatus} onValueChange={(v) => setHousingStatus(v as HousingStatus)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="renting">Renting privately</SelectItem>
            <SelectItem value="social-housing">Social / council housing</SelectItem>
            <SelectItem value="owning">Own my home</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">Employment status</Label>
        <Select value={employmentStatus} onValueChange={(value) => setEmploymentStatus(value as EmploymentStatus)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="employed">Employed</SelectItem>
            <SelectItem value="self-employed">Self-employed</SelectItem>
            <SelectItem value="mixed">Mix of both</SelectItem>
            <SelectItem value="not-working">Not currently working</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3 rounded-xl border border-border bg-background px-4 py-3">
        <div className="flex items-start gap-3">
          <Checkbox
            id="benefits-health-condition"
            checked={hasHealthCondition}
            onCheckedChange={(checked) => setHasHealthCondition(checked === true)}
            className="mt-0.5"
          />
          <div>
            <Label htmlFor="benefits-health-condition" className="text-sm font-medium text-foreground">
              Disability or long-term health condition affects daily life
            </Label>
            <p className="text-[11px] text-muted-foreground mt-1">
              This helps Adulting separate the main disability routes like PIP, child DLA, and NHS cost help without asking for medical detail.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            id="benefits-care-role"
            checked={providesCare}
            onCheckedChange={(checked) => setProvidesCare(checked === true)}
            className="mt-0.5"
          />
          <div>
            <Label htmlFor="benefits-care-role" className="text-sm font-medium text-foreground">
              I regularly care for a disabled or unwell person
            </Label>
            <p className="text-[11px] text-muted-foreground mt-1">
              This keeps Carer&apos;s Allowance and Carer&apos;s Credit visible as separate routes instead of hiding them in one generic result.
            </p>
          </div>
        </div>
      </div>

      <Button onClick={handleSubmit} className="w-full bg-primary text-primary-foreground">
        Check my entitlements
      </Button>

      <p className="text-[11px] text-muted-foreground text-center">
        This is an estimate only — we don't store or share this information with anyone.
      </p>
    </div>
  );
}
